import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../../../lib/utils';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraText } from '../ensigns/typography';
import { chartToneVar, SpecraChartFrame } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
/** The worst (largest) aspect ratio of a row of areas laid along a side of length `side`. */ function worst(row, side) {
    const sum = row.reduce((a, v)=>a + v, 0);
    const max = Math.max(...row);
    const min = Math.min(...row);
    const s2 = side * side;
    const sum2 = sum * sum;
    return Math.max(s2 * max / sum2, sum2 / (s2 * min));
}
/**
 * Squarified treemap layout (Bruls, Huizing, van Wijk): each item gets a
 * rectangle in a width × height box with area proportional to its value,
 * filled largest first. Returns one rect per item, in the input order; items
 * without a positive value get an empty rect.
 */ export function squarify(items, width, height) {
    const out = items.map(()=>({
            x: 0,
            y: 0,
            w: 0,
            h: 0
        }));
    const order = items.map((it, i)=>({
            i,
            v: it.value
        })).filter((e)=>Number.isFinite(e.v) && e.v > 0).sort((a, b)=>b.v - a.v);
    const total = order.reduce((a, e)=>a + e.v, 0);
    if (!total || width <= 0 || height <= 0) return out;
    const scale = width * height / total;
    const areas = order.map((e)=>({
            i: e.i,
            a: e.v * scale
        }));
    let x = 0;
    let y = 0;
    let w = width;
    let h = height;
    let k = 0;
    while(k < areas.length){
        const side = Math.min(w, h);
        const row = [
            areas[k]
        ];
        k++;
        while(k < areas.length){
            const next = [
                ...row.map((r)=>r.a),
                areas[k].a
            ];
            if (worst(next, side) > worst(row.map((r)=>r.a), side)) break;
            row.push(areas[k]);
            k++;
        }
        const sum = row.reduce((a, r)=>a + r.a, 0);
        if (w >= h) {
            // A column on the left of the remaining box.
            const colW = sum / h;
            let cy = y;
            for (const r of row){
                const rh = r.a / colW;
                out[r.i] = {
                    x,
                    y: cy,
                    w: colW,
                    h: rh
                };
                cy += rh;
            }
            x += colW;
            w -= colW;
        } else {
            // A row along the top of the remaining box.
            const rowH = sum / w;
            let cx = x;
            for (const r of row){
                const rw = r.a / rowH;
                out[r.i] = {
                    x: cx,
                    y,
                    w: rw,
                    h: rowH
                };
                cx += rw;
            }
            y += rowH;
            h -= rowH;
        }
    }
    return out;
}
const grouped = (v)=>v.toLocaleString('en-US');
/** Group shades: the heat ramp from strongest down, then round again. */ const GROUP_FILL = [
    'var(--chart-heat-4)',
    'var(--chart-heat-3)',
    'var(--chart-heat-2)',
    'var(--chart-heat-1)'
];
const W = 200;
const H = 100;
const pct = (n, of)=>`${n / of * 100}%`;
/** Parts of a whole as squarified tiles, with a table of the same numbers. */ export function SpecraTreemap({ items, format = grouped, source, title, description, insight, className, 'data-testid': testId }) {
    const shown = items.filter((it)=>Number.isFinite(it.value) && it.value > 0);
    const left = items.length - shown.length;
    const total = shown.reduce((a, it)=>a + it.value, 0);
    const rects = squarify(shown, W, H);
    const groups = [
        ...new Set(shown.map((it)=>it.group).filter((g)=>!!g))
    ];
    const share = (v)=>{
        const p = v / total * 100;
        return p < 1 ? '<1%' : `${Math.round(p)}%`;
    };
    const fillOf = (it)=>{
        if (it.tone) {
            return {
                fill: chartToneVar[it.tone],
                strong: it.tone !== 'pending' && it.tone !== 'warn'
            };
        }
        if (it.group) {
            const g = groups.indexOf(it.group) % GROUP_FILL.length;
            return {
                fill: GROUP_FILL[g],
                strong: g < 2
            };
        }
        return {
            fill: chartToneVar.series,
            strong: true
        };
    };
    const hasGroups = groups.length > 0;
    const table = {
        columns: [
            {
                key: 'label',
                label: 'Item'
            },
            ...hasGroups ? [
                {
                    key: 'group',
                    label: 'Group'
                }
            ] : [],
            {
                key: 'value',
                label: 'Value',
                numeric: true
            },
            {
                key: 'share',
                label: 'Share',
                numeric: true
            }
        ],
        rows: [
            ...shown
        ].sort((a, b)=>b.value - a.value).map((it)=>({
                id: it.id,
                cells: {
                    label: it.label,
                    group: it.group ?? '',
                    value: format(it.value),
                    share: share(it.value)
                }
            }))
    };
    const legend = hasGroups ? /*#__PURE__*/ _jsx("ul", {
        "aria-label": "Legend",
        className: "flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-1)]",
        children: groups.map((g, i)=>/*#__PURE__*/ _jsxs("li", {
                className: "flex items-center gap-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        "aria-hidden": "true",
                        className: "inline-block size-2.5 shrink-0 rounded-[var(--radius-xs)]",
                        style: {
                            background: GROUP_FILL[i % GROUP_FILL.length]
                        }
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: g
                    })
                ]
            }, g))
    }) : undefined;
    const footnote = left ? `${left.toLocaleString('en-US')} ${left === 1 ? 'item' : 'items'} with no positive value left out.` : undefined;
    return /*#__PURE__*/ _jsxs(SpecraChartFrame, {
        className: className,
        "data-testid": testId,
        description: description,
        insight: insight,
        legend: legend,
        table: table,
        title: title,
        children: [
            shown.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                tone: "muted",
                children: "No positive values to show."
            }) : /*#__PURE__*/ _jsx(SpecraTooltipProvider, {
                delayDuration: 100,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "relative aspect-[2/1] w-full overflow-hidden rounded-[var(--radius-md)]",
                    "data-treemap": "",
                    children: shown.map((it, i)=>{
                        const r = rects[i];
                        const { fill, strong } = fillOf(it);
                        // Room for a label: roughly 14% wide and 18% tall of the box.
                        const roomy = r.w / W >= 0.14 && r.h / H >= 0.18;
                        const roomier = roomy && r.h / H >= 0.3;
                        return /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: /*#__PURE__*/ _jsxs("span", {
                                className: "block",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "block font-semibold",
                                        children: it.label
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "block",
                                        children: [
                                            format(it.value),
                                            " · ",
                                            share(it.value)
                                        ]
                                    }),
                                    it.group ? /*#__PURE__*/ _jsx("span", {
                                        className: "block",
                                        children: it.group
                                    }) : null
                                ]
                            }),
                            delayDuration: 100,
                            children: /*#__PURE__*/ _jsx("span", {
                                "aria-label": `${it.label}: ${format(it.value)}, ${share(it.value)} of the total${it.group ? `, ${it.group}` : ''}`,
                                className: cn('absolute flex flex-col items-start justify-start overflow-hidden border border-[color:var(--surface-card)] p-[var(--space-1)] transition-opacity hover:opacity-90', specraFocusRing),
                                "data-item-id": it.id,
                                role: "img",
                                style: {
                                    left: pct(r.x, W),
                                    top: pct(r.y, H),
                                    width: pct(r.w, W),
                                    height: pct(r.h, H),
                                    background: fill
                                },
                                // biome-ignore lint/a11y/noNoninteractiveTabindex: focusable so its tooltip opens from the keyboard
                                tabIndex: 0,
                                children: roomy ? /*#__PURE__*/ _jsxs("span", {
                                    "aria-hidden": "true",
                                    className: cn('flex min-w-0 max-w-full flex-col', strong ? 'text-[color:var(--chart-heat-ink-strong)]' : 'text-foreground'),
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            className: "truncate font-medium text-inherit",
                                            size: "meta",
                                            children: it.label
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            className: "truncate tabular-nums text-inherit",
                                            size: "micro",
                                            children: [
                                                format(it.value),
                                                roomier ? ` · ${share(it.value)}` : ''
                                            ]
                                        })
                                    ]
                                }) : null
                            })
                        }, it.id);
                    })
                })
            }),
            source || footnote ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: [
                    source,
                    footnote
                ].filter(Boolean).join(' · ')
            }) : null
        ]
    });
}
